/**
 * Daily Drive gamification widget.
 *
 * ============================================================================
 * Accessibility checklist for new dashboard widgets / themes
 * ============================================================================
 * When adding a new theme renderer, EOD-style banner, or any other dashboard
 * card here, follow the same pattern that the milestone glance + theme tiles
 * use so reps on assistive tech get parity with sighted reps:
 *
 *   1. Every interactive control (button, link, focusable row) must have a
 *      descriptive `aria-label` — never rely on `title=` alone, since `title`
 *      is unreliable for screen readers and invisible to keyboard / touch
 *      users. Icon-only buttons (mute, dismiss, theme cycle, prev/next, etc)
 *      always need an explicit aria-label that describes the action *and* the
 *      resulting state when toggleable (e.g. "Mute Daily Drive sounds").
 *
 *   2. State that is conveyed by color (ahead/behind, up/down delta, goal
 *      hit/missed) must also be conveyed in text — either visible text in
 *      the tile or an `aria-label` / `sr-only` span. Icons that duplicate
 *      that text (✓, ↑, ★) should be `aria-hidden="true"`.
 *
 *   3. Tiles that bundle a chart + a few numbers (the equalizer bars, the
 *      test tubes, the marquee LEDs, the EOD per-metric tiles) get
 *      `role="group"` + an `aria-label` that summarizes count, goal, %, and
 *      pace, so a screen reader announces the whole tile as one chunk
 *      instead of reading every decorative chart segment.
 *
 *   4. Decorative chart fills, bars, bubbles, and animation containers must
 *      be `aria-hidden="true"` so SR users don't hear the visualization
 *      twice. The `aria-label` on the parent tile is the source of truth.
 *
 *   5. Honor `reduceMotion` for every animation, popover, and tooltip
 *      transition. Pass it down to children that animate, and disable
 *      framer-motion transitions (`{ duration: 0 }`) and Radix animation
 *      classes (`style={{ animation: 'none' }}` on TooltipContent) when it
 *      is on. Looping animations (marquee scroll, bubbles) must stop.
 *
 *   6. If a row carries information visible only on hover (a tooltip /
 *      popover), the row itself must be focusable (`tabIndex={0}` +
 *      `role="group"`) and carry the same coaching message in its
 *      `aria-label`, so a keyboard-only rep gets the same context the
 *      sighted rep gets on hover.
 * ============================================================================
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, Activity, FlaskConical, Megaphone, AudioLines, Volume2, VolumeX, Settings as SettingsIcon, X, Zap, Target, Star } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  GamificationSnapshot,
  GamificationMetricKey,
  GamificationPeriodKey,
  GamificationTheme,
  SnapshotMetric,
  SnapshotPeriod,
  METRIC_LABELS,
  METRIC_SUBTITLES,
  METRIC_COLORS,
  formatMetricValue,
} from "./types";
import { playTickSound } from "./sound-utils";

const THEME_OPTIONS: Array<{ value: GamificationTheme; label: string; icon: React.ComponentType<{ className?: string }> }> = [
  { value: "equalizer", label: "Equalizer", icon: AudioLines },
  { value: "test_tubes", label: "Test Tubes", icon: FlaskConical },
  { value: "marquee", label: "Marquee", icon: Megaphone },
];

function paceColor(status: SnapshotMetric["paceStatus"]): string {
  if (status === "ahead") return "text-emerald-600 dark:text-emerald-400";
  if (status === "behind") return "text-amber-600 dark:text-amber-400";
  return "text-blue-600 dark:text-blue-400";
}

function paceLabel(status: SnapshotMetric["paceStatus"]): string {
  if (status === "ahead") return "Ahead";
  if (status === "behind") return "Behind";
  return "On track";
}

// Compose a single screen-reader-friendly description for a metric tile.
// The visual themes (equalizer bars, test tubes, marquee LEDs, EOD recap
// tiles) all wrap the same numbers in different chrome — this helper keeps
// the SR experience consistent across them so every theme passes the same
// a11y bar as the milestone glance (see checklist comment at top of file).
function metricTileAriaLabel(
  metric: SnapshotMetric,
  metricKey: GamificationMetricKey,
): string {
  const label = METRIC_LABELS[metricKey];
  const pct = Math.round(Math.min(100, metric.pct * 100));
  const pace = paceLabel(metric.paceStatus);
  if (metric.pct >= 1) {
    return `${label}: goal hit, ${metric.count} of ${metric.goal} (${pct}%), ${pace.toLowerCase()} pace, projected ${metric.paceForecast}`;
  }
  return `${label}: ${metric.count} of ${metric.goal} (${pct}%), ${pace.toLowerCase()} pace, projected ${metric.paceForecast}`;
}

function MetricSummary({
  metric,
  metricKey,
}: {
  metric: SnapshotMetric;
  metricKey: GamificationMetricKey;
}) {
  const countStr = formatMetricValue(metricKey, metric.count);
  const goalStr = formatMetricValue(metricKey, metric.goal);
  const paceStr = formatMetricValue(metricKey, metric.paceForecast);
  return (
    <div className="flex items-center justify-between text-xs sm:text-sm">
      <div className="flex flex-col">
        <span className="font-semibold text-foreground" data-testid={`text-${metricKey}-count`}>
          {countStr}
          <span className="text-muted-foreground font-normal">/{goalStr}</span>
        </span>
        <span className="text-muted-foreground text-[10px] sm:text-xs">{METRIC_SUBTITLES[metricKey]}</span>
      </div>
      <Badge variant="outline" className={cn("text-[10px] sm:text-xs font-medium", paceColor(metric.paceStatus))} data-testid={`badge-${metricKey}-pace`}>
        {paceLabel(metric.paceStatus)} · pace {paceStr}
      </Badge>
    </div>
  );
}

// =============================================================================
// EQUALIZER THEME — vertical bars with animated heights and pulse on update
// =============================================================================
function EqualizerTheme({ period, reduceMotion }: { period: SnapshotPeriod; reduceMotion: boolean }) {
  const metricKeys: GamificationMetricKey[] = ["activity", "pipeline", "wins", "revenueBooked"];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {metricKeys.map((mk) => {
        const m = period[mk];
        const fillPct = Math.min(100, m.pct * 100);
        return (
          <div
            key={mk}
            className="rounded-lg bg-muted/40 dark:bg-muted/20 p-3 flex flex-col gap-2 border"
            data-testid={`equalizer-${mk}`}
            role="group"
            aria-label={metricTileAriaLabel(m, mk)}
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{METRIC_LABELS[mk]}</span>
              {m.pct >= 1 && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" aria-hidden="true" />}
            </div>
            <div className="relative h-32 sm:h-40 w-full overflow-hidden rounded-md bg-background border" aria-hidden="true">
              {/* "Bars" — five vertical segments, the lower ones fill first */}
              <div className="absolute inset-0 flex items-end gap-1 p-1.5">
                {[0, 1, 2, 3, 4].map((i) => {
                  const segmentThreshold = (i + 1) * 20;
                  const segmentFill = Math.max(0, Math.min(20, fillPct - i * 20)) / 20;
                  return (
                    <motion.div
                      key={i}
                      className="flex-1 rounded-sm origin-bottom"
                      style={{
                        backgroundColor: METRIC_COLORS[mk],
                        opacity: 0.2 + 0.8 * segmentFill,
                      }}
                      initial={false}
                      animate={{
                        height: `${(20 + 80 * (i / 4)) * Math.max(0.05, segmentFill || 0.05)}%`,
                      }}
                      transition={
                        reduceMotion
                          ? { duration: 0 }
                          : { type: "spring", stiffness: 140, damping: 16, delay: i * 0.04 }
                      }
                    />
                  );
                })}
              </div>
              {/* Goal line */}
              <div className="absolute left-0 right-0 top-0 h-px bg-foreground/20" />
              <div className="absolute right-1 top-1 text-[10px] text-muted-foreground bg-background/70 px-1 rounded" data-testid={`text-${mk}-pct`}>
                {Math.round(fillPct)}%
              </div>
            </div>
            <MetricSummary metric={m} metricKey={mk} />
          </div>
        );
      })}
    </div>
  );
}

// =============================================================================
// TEST TUBES THEME — vertical liquid tubes with bubbles
// =============================================================================
function TestTubesTheme({ period, reduceMotion }: { period: SnapshotPeriod; reduceMotion: boolean }) {
  const metricKeys: GamificationMetricKey[] = ["activity", "pipeline", "wins", "revenueBooked"];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {metricKeys.map((mk) => {
        const m = period[mk];
        const fillPct = Math.min(100, m.pct * 100);
        return (
          <div
            key={mk}
            className="rounded-lg bg-muted/40 dark:bg-muted/20 p-3 flex flex-col gap-2 border"
            data-testid={`tube-${mk}`}
            role="group"
            aria-label={metricTileAriaLabel(m, mk)}
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{METRIC_LABELS[mk]}</span>
              {m.pct >= 1 && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" aria-hidden="true" />}
            </div>
            <div className="relative h-32 sm:h-40 w-full flex justify-center" aria-hidden="true">
              <div className="relative w-12 sm:w-14 h-full">
                {/* Tube outline */}
                <div className="absolute inset-x-0 top-0 bottom-2 rounded-t-md rounded-b-full border-2 border-foreground/30 bg-background overflow-hidden">
                  {/* Liquid */}
                  <motion.div
                    className="absolute inset-x-0 bottom-0"
                    style={{ backgroundColor: METRIC_COLORS[mk] }}
                    initial={false}
                    animate={{ height: `${Math.max(2, fillPct)}%` }}
                    transition={reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 100, damping: 18 }}
                  >
                    {/* Liquid surface wave */}
                    <div className="absolute -top-1 left-0 right-0 h-2 opacity-80" style={{
                      background: `radial-gradient(circle at 50% 100%, ${METRIC_COLORS[mk]} 60%, transparent 70%)`
                    }} />
                    {/* Bubbles */}
                    {!reduceMotion && fillPct > 5 && [0, 1, 2].map((i) => (
                      <motion.div
                        key={i}
                        className="absolute rounded-full bg-white/50"
                        style={{
                          left: `${20 + i * 25}%`,
                          width: 4 + i,
                          height: 4 + i,
                        }}
                        initial={{ bottom: "5%", opacity: 0 }}
                        animate={{ bottom: ["5%", "85%"], opacity: [0, 0.8, 0] }}
                        transition={{ duration: 2 + i * 0.5, repeat: Infinity, delay: i * 0.6, ease: "easeOut" }}
                      />
                    ))}
                  </motion.div>
                </div>
                {/* Top opening */}
                <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-8 h-1.5 rounded-sm border-2 border-foreground/30 bg-background" />
                {/* Fill % readout */}
                <div className="absolute -right-1 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-muted-foreground bg-background/80 px-1 rounded" data-testid={`text-${mk}-pct`}>
                  {Math.round(fillPct)}%
                </div>
              </div>
            </div>
            <MetricSummary metric={m} metricKey={mk} />
          </div>
        );
      })}
    </div>
  );
}

// =============================================================================
// MARQUEE THEME — horizontal LED-style bars with running ticker
// =============================================================================
function MarqueeTheme({ period, reduceMotion }: { period: SnapshotPeriod; reduceMotion: boolean }) {
  const metricKeys: GamificationMetricKey[] = ["activity", "pipeline", "wins", "revenueBooked"];
  return (
    <div className="space-y-3">
      {metricKeys.map((mk) => {
        const m = period[mk];
        const fillPct = Math.min(100, m.pct * 100);
        const segments = 20;
        const litCount = Math.round((fillPct / 100) * segments);
        return (
          <div
            key={mk}
            className="rounded-lg bg-zinc-900 dark:bg-zinc-950 text-amber-100 p-3 flex flex-col gap-2 border border-amber-700/40 shadow-inner"
            data-testid={`marquee-${mk}`}
            role="group"
            aria-label={metricTileAriaLabel(m, mk)}
          >
            <div className="flex items-center justify-between text-xs">
              <span className="font-bold uppercase tracking-widest" style={{ color: METRIC_COLORS[mk] }}>
                ★ {METRIC_LABELS[mk]} ★
              </span>
              <span className="font-mono font-bold" style={{ color: METRIC_COLORS[mk] }} data-testid={`text-${mk}-count`}>
                {formatMetricValue(mk, m.count)} / {formatMetricValue(mk, m.goal)}
              </span>
            </div>
            <div className="flex gap-0.5 h-6" aria-hidden="true">
              {Array.from({ length: segments }).map((_, i) => {
                const lit = i < litCount;
                return (
                  <motion.div
                    key={i}
                    className="flex-1 rounded-sm"
                    initial={false}
                    animate={{
                      backgroundColor: lit ? METRIC_COLORS[mk] : "rgba(255,255,255,0.08)",
                      boxShadow: lit ? `0 0 6px ${METRIC_COLORS[mk]}` : "none",
                    }}
                    transition={reduceMotion ? { duration: 0 } : { duration: 0.25, delay: i * 0.015 }}
                  />
                );
              })}
            </div>
            <div className="overflow-hidden" aria-hidden="true">
              <motion.div
                className={cn("text-[10px] font-mono uppercase tracking-widest whitespace-nowrap", paceColor(m.paceStatus))}
                animate={reduceMotion ? {} : { x: ["0%", "-50%"] }}
                transition={reduceMotion ? { duration: 0 } : { duration: 18, repeat: Infinity, ease: "linear" }}
              >
                {paceLabel(m.paceStatus)} · projected {formatMetricValue(mk, m.paceForecast)} · {Math.round(fillPct)}% complete · {METRIC_SUBTITLES[mk]} · &nbsp;&nbsp;&nbsp;
                {paceLabel(m.paceStatus)} · projected {formatMetricValue(mk, m.paceForecast)} · {Math.round(fillPct)}% complete · {METRIC_SUBTITLES[mk]} · &nbsp;&nbsp;&nbsp;
              </motion.div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const THEME_RENDERERS: Record<GamificationTheme, React.ComponentType<{ period: SnapshotPeriod; reduceMotion: boolean }>> = {
  equalizer: EqualizerTheme,
  test_tubes: TestTubesTheme,
  marquee: MarqueeTheme,
};

// =============================================================================
// MILESTONE GLANCE — compact weekly + monthly progress shown alongside daily
// so reps always know how close they are to the longer-window goals without
// having to switch tabs. Honors reduceMotion (skips bar fill animation and
// disables tooltip enter/exit animation) and is intentionally silent (no
// sounds) to respect silentMode. Each row is a focusable group with an
// aria-label carrying the "X more to go" coaching message so screen-reader
// users get the same guidance the visual tooltip provides on hover/focus.
// =============================================================================
const MILESTONE_PERIODS: Array<{ key: Exclude<GamificationPeriodKey, "daily">; label: string }> = [
  { key: "weekly", label: "Weekly" },
  { key: "monthly", label: "Monthly" },
];

function MilestoneGlance({
  snapshot,
  reduceMotion,
}: {
  snapshot: GamificationSnapshot;
  reduceMotion: boolean;
}) {
  const metricKeys: GamificationMetricKey[] = ["activity", "pipeline", "wins", "revenueBooked"];
  return (
    <div className="space-y-1.5 pt-1" data-testid="milestone-glance">
      <div className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
        <Target className="h-3 w-3" />
        <span>Pace toward milestones</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {MILESTONE_PERIODS.map(({ key, label }) => {
          const p = snapshot[key];
          return (
            <div
              key={key}
              className="rounded-md border bg-muted/30 dark:bg-muted/10 p-2 space-y-1.5"
              data-testid={`milestone-${key}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {label}
                </span>
              </div>
              <div className="space-y-1">
                {metricKeys.map((mk) => {
                  const m = p[mk];
                  const pct = Math.min(100, m.pct * 100);
                  const remaining = Math.max(0, m.goal - m.count);
                  const remainingLabel = remaining > 0 ? `${remaining} to go` : "Hit ✓";
                  const tooltip =
                    remaining > 0
                      ? `${remaining} more ${METRIC_LABELS[mk].toLowerCase()} to hit ${label.toLowerCase()} goal · ${paceLabel(m.paceStatus).toLowerCase()} pace`
                      : `${label} ${METRIC_LABELS[mk].toLowerCase()} goal hit`;
                  return (
                    <Tooltip key={mk}>
                      <TooltipTrigger asChild>
                        <div
                          tabIndex={0}
                          role="group"
                          aria-label={tooltip}
                          className="flex items-center gap-2 text-[10px] rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                          data-testid={`milestone-${key}-${mk}`}
                        >
                          <span className="w-12 shrink-0 truncate text-muted-foreground" aria-hidden="true">
                            {METRIC_LABELS[mk]}
                          </span>
                          <div className="flex-1 h-1.5 rounded-full bg-background overflow-hidden border min-w-0" aria-hidden="true">
                            <motion.div
                              className="h-full"
                              style={{ backgroundColor: METRIC_COLORS[mk] }}
                              initial={false}
                              animate={{ width: `${pct}%` }}
                              transition={
                                reduceMotion
                                  ? { duration: 0 }
                                  : { type: "spring", stiffness: 120, damping: 18 }
                              }
                            />
                          </div>
                          <span
                            className={cn("font-semibold tabular-nums shrink-0 w-9 text-right", paceColor(m.paceStatus))}
                            data-testid={`text-milestone-${key}-${mk}-pct`}
                            aria-hidden="true"
                          >
                            {Math.round(pct)}%
                          </span>
                          <span
                            className={cn(
                              "shrink-0 w-16 text-right font-medium uppercase tracking-wide text-[9px]",
                              paceColor(m.paceStatus),
                            )}
                            data-testid={`text-milestone-${key}-${mk}-pace`}
                            aria-hidden="true"
                          >
                            {paceLabel(m.paceStatus)}
                          </span>
                          <span
                            className="text-muted-foreground tabular-nums shrink-0 w-12 text-right"
                            data-testid={`text-milestone-${key}-${mk}-remaining`}
                            aria-hidden="true"
                          >
                            {remainingLabel}
                          </span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        data-testid={`tooltip-milestone-${key}-${mk}`}
                        // Honor the user's reduce-motion preference by
                        // suppressing the shadcn TooltipContent's default
                        // `animate-in fade-in-0 zoom-in-95` entry animation.
                        // We can't rely on the inline `style` prop here —
                        // Radix's Popper actively overwrites `animation`
                        // back to `undefined` once the tooltip is
                        // positioned — so we override via tailwind-merge
                        // by passing a higher-priority `animate-none`
                        // (the `!` prefix marks it `!important` so it
                        // wins over the base `animate-in` declaration).
                        className={cn(reduceMotion && "!animate-none")}
                      >
                        {tooltip}
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const THEME_ICONS: Record<GamificationTheme, React.ComponentType<{ className?: string }>> = {
  equalizer: AudioLines,
  test_tubes: FlaskConical,
  marquee: Megaphone,
};

const THEME_LABELS: Record<GamificationTheme, string> = {
  equalizer: "Equalizer",
  test_tubes: "Test Tubes",
  marquee: "Marquee",
};

// =============================================================================
// END-OF-DAY SUMMARY BANNER
// =============================================================================
type EodSummary = {
  available: boolean;
  date?: string;
  daily?: SnapshotPeriod;
  theme?: GamificationTheme;
};

export function EndOfDaySummaryBanner() {
  const { data } = useQuery<EodSummary>({
    queryKey: ["/api/gamification/end-of-day-summary"],
    refetchOnWindowFocus: true,
  });
  const dismiss = useMutation({
    mutationFn: async (date: string) => apiRequest("POST", "/api/gamification/end-of-day-summary/dismiss", { date }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/gamification/end-of-day-summary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/gamification/my-prefs"] });
    },
  });

  if (!data?.available || !data.daily || !data.date) return null;

  const d = data.daily;
  const totalAchieved = (["activity", "pipeline", "wins", "revenueBooked"] as GamificationMetricKey[]).filter((k) => d[k].pct >= 1).length;

  return (
    <Card
      className="border-l-4 border-l-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/30"
      data-testid="banner-eod-summary"
      role="region"
      aria-label={`Yesterday's recap, ${totalAchieved} of 4 goals hit`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2">
              <Trophy className="h-4 w-4 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
              <span className="font-semibold text-sm" data-testid="text-eod-title">
                Yesterday's recap · {totalAchieved}/4 goals hit
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              {(["activity", "pipeline", "wins", "revenueBooked"] as GamificationMetricKey[]).map((k) => {
                const m = d[k];
                const pct = Math.round(m.pct * 100);
                const countStr = formatMetricValue(k, m.count);
                const goalStr = formatMetricValue(k, m.goal);
                const tileLabel =
                  m.pct >= 1
                    ? `${METRIC_LABELS[k]}: goal hit, ${countStr} of ${goalStr}`
                    : `${METRIC_LABELS[k]}: ${countStr} of ${goalStr} (${pct}%), goal not hit`;
                return (
                  <div
                    key={k}
                    className="bg-background rounded p-2 border"
                    data-testid={`eod-${k}`}
                    role="group"
                    aria-label={tileLabel}
                  >
                    <div className="text-muted-foreground uppercase tracking-wide text-[10px]">{METRIC_LABELS[k]}</div>
                    <div className="font-bold text-base">
                      {countStr}
                      <span className="text-muted-foreground font-normal text-xs">/{goalStr}</span>
                    </div>
                    <div
                      className={cn(
                        "text-[10px]",
                        m.pct >= 1 ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground",
                      )}
                      aria-hidden="true"
                    >
                      {m.pct >= 1 ? "Goal hit ✓" : `${pct}%`}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => dismiss.mutate(data.date!)}
            data-testid="button-dismiss-eod"
            aria-label="Dismiss yesterday's recap"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// MAIN GAMIFICATION WIDGET
// =============================================================================
export function GamificationWidget({ targetUserId }: { targetUserId?: string } = {}) {
  const { toast } = useToast();
  const [period, setPeriod] = useState<GamificationPeriodKey>("daily");
  const queryKey = targetUserId
    ? ["/api/gamification/snapshot", targetUserId]
    : ["/api/gamification/snapshot"];

  const { data: snapshot, isLoading } = useQuery<GamificationSnapshot>({
    queryKey,
    queryFn: async () => {
      const url = targetUserId
        ? `/api/gamification/snapshot?userId=${encodeURIComponent(targetUserId)}`
        : "/api/gamification/snapshot";
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load gamification snapshot");
      return res.json();
    },
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const updatePrefs = useMutation({
    mutationFn: async (updates: Record<string, unknown>) =>
      apiRequest("PATCH", "/api/gamification/my-prefs", updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/gamification/my-prefs"] });
      queryClient.invalidateQueries({ queryKey: ["/api/gamification/snapshot"] });
    },
  });

  // Track previous snapshot to play a soft "tick" when activity is logged while
  // the widget is visible. Goal-crossing milestones (toast + confetti + win
  // sound) are handled globally by GlobalGamificationCelebrations so they fire
  // from any page, not just the dashboard. Skip ticks when viewing another
  // rep's widget — those events aren't ours to celebrate.
  const prevSnapRef = useRef<GamificationSnapshot | null>(null);
  useEffect(() => {
    if (!snapshot || !snapshot.enabled || targetUserId) {
      prevSnapRef.current = snapshot ?? null;
      return;
    }
    const prev = prevSnapRef.current;
    if (prev && snapshot.soundsAllowed) {
      const dailyKeys: GamificationMetricKey[] = ["activity", "pipeline", "wins"];
      let triggered = false;
      for (const k of dailyKeys) {
        if (snapshot.daily[k].count > prev.daily[k].count) {
          triggered = true;
          break;
        }
      }
      if (triggered) {
        playTickSound();
      }
    }
    prevSnapRef.current = snapshot;
  }, [snapshot, targetUserId]);

  if (isLoading) {
    return (
      <Card data-testid="gamification-widget-loading">
        <CardContent className="p-4 space-y-3">
          <Skeleton className="h-6 w-40" />
          <div className="grid grid-cols-3 gap-3">
            <Skeleton className="h-40" />
            <Skeleton className="h-40" />
            <Skeleton className="h-40" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!snapshot || !snapshot.enabled) {
    return null;
  }

  const ThemeRenderer = THEME_RENDERERS[snapshot.theme] ?? EqualizerTheme;
  const ThemeIcon = THEME_ICONS[snapshot.theme] ?? AudioLines;

  const currentPeriod = snapshot[period];

  return (
    <Card data-testid="gamification-widget" className="overflow-hidden">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Trophy className="h-4 w-4 text-amber-500" aria-hidden="true" />
            <h3 className="text-sm font-semibold" data-testid="text-widget-title">
              Daily Drive · {THEME_LABELS[snapshot.theme]}
            </h3>
          </div>
          {!targetUserId && (
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                aria-label={
                  snapshot.silentMode
                    ? "Unmute Daily Drive sounds"
                    : "Mute Daily Drive sounds"
                }
                aria-pressed={snapshot.silentMode}
                onClick={() => updatePrefs.mutate({ silentMode: !snapshot.silentMode })}
                data-testid="button-toggle-silent"
              >
                {snapshot.silentMode ? (
                  <VolumeX className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <Volume2 className="h-4 w-4" aria-hidden="true" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                aria-label={`Switch theme (currently ${THEME_LABELS[snapshot.theme]})`}
                onClick={() => {
                  const order: GamificationTheme[] = ["equalizer", "test_tubes", "marquee"];
                  const idx = order.indexOf(snapshot.theme);
                  const next = order[(idx + 1) % order.length];
                  updatePrefs.mutate({ theme: next });
                  toast({ title: `Theme: ${THEME_LABELS[next]}`, duration: 1500 });
                }}
                data-testid="button-cycle-theme"
              >
                <ThemeIcon className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          )}
        </div>

        <Tabs value={period} onValueChange={(v) => setPeriod(v as GamificationPeriodKey)} className="w-full">
          <TabsList className="grid grid-cols-3 h-8">
            <TabsTrigger value="daily" className="text-xs" data-testid="tab-daily">Daily</TabsTrigger>
            <TabsTrigger value="weekly" className="text-xs" data-testid="tab-weekly">Weekly</TabsTrigger>
            <TabsTrigger value="monthly" className="text-xs" data-testid="tab-monthly">Monthly</TabsTrigger>
          </TabsList>
          <TabsContent value={period} className="mt-3">
            <AnimatePresence mode="wait">
              <motion.div
                key={`${snapshot.theme}-${period}`}
                initial={snapshot.reduceMotion ? false : { opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={snapshot.reduceMotion ? undefined : { opacity: 0, y: -4 }}
                transition={{ duration: snapshot.reduceMotion ? 0 : 0.18 }}
              >
                <ThemeRenderer period={currentPeriod} reduceMotion={snapshot.reduceMotion} />
              </motion.div>
            </AnimatePresence>
          </TabsContent>
        </Tabs>

        <MilestoneGlance snapshot={snapshot} reduceMotion={snapshot.reduceMotion} />

        {snapshot.qualityFlags.calls > 0 && (
          <p className="text-[11px] text-amber-600 dark:text-amber-400" data-testid="text-quality-flag">
            {snapshot.qualityFlags.calls} call(s) today are missing notes and weren't credited.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export { THEME_OPTIONS };
